/* eslint-disable no-console */
/**
 * Secure Logger Utility
 *
 * SECURITY: This logger only outputs in development mode to prevent
 * sensitive information from being exposed in production browser consoles.
 *
 * In production, errors are sent to Sentry for monitoring.
 */
import { captureError } from './sentry.mjs';

const isDevelopment = process.env.NODE_ENV === 'development';
const isDebugEnabled = isDevelopment && process.env.REACT_APP_DEBUG_MODE === 'true';

// Patterns to detect and redact sensitive information
const SENSITIVE_PATTERNS = [
    /password/i,
    /token/i,
    /secret/i,
    /apikey/i,
    /authorization/i,
    /bearer/i,
    /credential/i,
    /ssn/i,
    /social.*security/i,
];

/**
 * Redact sensitive values from objects before logging
 */
const redactSensitiveData = (data) => {
    if (typeof data !== 'object' || data === null) {
        return data;
    }

    const redacted = Array.isArray(data) ? [...data] : { ...data };

    for (const key in redacted) {
        if (SENSITIVE_PATTERNS.some(pattern => pattern.test(key))) {
            redacted[key] = '[REDACTED]';
        } else if (typeof redacted[key] === 'object') {
            redacted[key] = redactSensitiveData(redacted[key]);
        }
    }

    return redacted;
};

/**
 * Development-only logger
 * Logs are only output when NODE_ENV is 'development'
 */
export const secureLogger = {
    /**
     * Log debug messages (only in development with debug mode enabled)
     */
    debug: (...args) => {
        if (isDevelopment && isDebugEnabled) {
            console.log('[DEBUG]', ...args.map(arg =>
                typeof arg === 'object' ? redactSensitiveData(arg) : arg
            ));
        }
    },

    /**
     * Log info messages (only in development)
     */
    info: (...args) => {
        if (isDevelopment) {
            console.log('[INFO]', ...args.map(arg =>
                typeof arg === 'object' ? redactSensitiveData(arg) : arg
            ));
        }
    },

    /**
     * Log warning messages (only in development)
     */
    warn: (...args) => {
        if (isDevelopment) {
            console.warn('[WARN]', ...args.map(arg =>
                typeof arg === 'object' ? redactSensitiveData(arg) : arg
            ));
        }
    },

    /**
     * Log error messages
     * In development: outputs to console
     * In production: sends to Sentry
     */
    error: (...args) => {
        if (isDevelopment) {
            console.error('[ERROR]', ...args.map(arg =>
                typeof arg === 'object' ? redactSensitiveData(arg) : arg
            ));
        } else {
            // In production, send to Sentry
            const errorMessage = args[0] || 'Unknown error';
            const errorData = args.slice(1).reduce((acc, arg) => {
                if (typeof arg === 'object') {
                    return { ...acc, ...redactSensitiveData(arg) };
                }
                return acc;
            }, {});
            captureError(new Error(errorMessage), errorData);
        }
    },

    /**
     * Log API request (only in development, with sensitive data redacted)
     */
    apiRequest: (method, url, data) => {
        if (isDevelopment && isDebugEnabled) {
            console.log(`[API ${method.toUpperCase()}]`, url, data ? redactSensitiveData(data) : '');
        }
    },

    /**
     * Log API response (only in development, with sensitive data redacted)
     */
    apiResponse: (status, url, data) => {
        if (isDevelopment && isDebugEnabled) {
            console.log(`[API RESPONSE ${status}]`, url, data ? redactSensitiveData(data) : '');
        }
    },
};

export default secureLogger;
